/**
 * App Links Page - Central hub for all PWA links
 * Back Office version for existing app structure
 */

import React, { useState } from 'react';
import { 
  ShoppingCart, 
  UtensilsCrossed, 
  Tablet, 
  LayoutDashboard,
  Globe,
  Package,
  Monitor,
  ExternalLink,
  Copy,
  Check,
  Settings,
  Info,
  Smartphone
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { copyText } from '@/lib/copyText';

// PWA Apps configuration
const PWA_APPS = [
  {
    id: 'pos',
    name: 'Point of Sale',
    shortName: 'POS',
    description: 'Full-featured POS for order entry, payments, and table management',
    subdomain: 'pos',
    icon: ShoppingCart,
    color: 'bg-emerald-600',
    colorLight: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    textColor: 'text-emerald-600',
    features: ['Order Entry', 'Table Management', 'Card/Cash Payments', 'Receipt Printing'],
    orientation: 'Landscape',
    idealFor: 'Cashier stations, tablets',
    category: 'staff'
  },
  {
    id: 'kds',
    name: 'Kitchen Display',
    shortName: 'KDS',
    description: 'Real-time kitchen ticket display with bump bar support',
    subdomain: 'kds',
    icon: UtensilsCrossed,
    color: 'bg-orange-600',
    colorLight: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    textColor: 'text-orange-600',
    features: ['Live Ticket Queue', 'Station Filtering', 'Sound Alerts', 'Bump Support'],
    orientation: 'Landscape',
    idealFor: 'Kitchen monitors, line cooks',
    category: 'staff'
  },
  {
    id: 'kiosk',
    name: 'Self-Service Kiosk',
    shortName: 'Kiosk',
    description: 'Customer-facing self-ordering kiosk with payment integration',
    subdomain: 'kiosk',
    icon: Tablet,
    color: 'bg-red-600',
    colorLight: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    textColor: 'text-red-600',
    features: ['Touch Menu', 'Self-Checkout', 'Loyalty Integration', 'Auto-Reset'],
    orientation: 'Portrait',
    idealFor: 'Self-service stations',
    category: 'customer'
  },
  {
    id: 'admin',
    name: 'Admin Dashboard',
    shortName: 'Admin',
    description: 'Complete back-office management for your restaurant',
    subdomain: 'app',
    icon: LayoutDashboard,
    color: 'bg-blue-600',
    colorLight: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    textColor: 'text-blue-600',
    features: ['Reports', 'Menu Management', 'Staff', 'Inventory'],
    orientation: 'Any',
    idealFor: 'Managers, owners',
    category: 'staff'
  },
  {
    id: 'order',
    name: 'Online Ordering',
    shortName: 'Order',
    description: 'Customer-facing online ordering for pickup and delivery',
    subdomain: 'order',
    icon: Globe,
    color: 'bg-purple-600',
    colorLight: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    textColor: 'text-purple-600',
    features: ['Mobile-First', 'Pickup/Delivery', 'Order Tracking', 'Rewards'],
    orientation: 'Portrait',
    idealFor: 'Customer mobile ordering',
    category: 'customer'
  },
  {
    id: 'expo',
    name: 'Expo Display',
    shortName: 'Expo',
    description: 'Food runner display for order assembly and hand-off',
    subdomain: 'expo',
    icon: Package,
    color: 'bg-green-600',
    colorLight: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    textColor: 'text-green-600',
    features: ['Ready Orders', 'Hand-off Tracking', 'Order Types', 'Sound Alerts'],
    orientation: 'Landscape',
    idealFor: 'Expo station, food runners',
    category: 'display'
  },
  {
    id: 'display',
    name: 'Front Desk Display',
    shortName: 'Display',
    description: 'Customer-facing order status display for lobby/counter',
    subdomain: 'display',
    icon: Monitor,
    color: 'bg-cyan-600',
    colorLight: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30',
    textColor: 'text-cyan-600',
    features: ['Order Status', 'Wait Times', 'Promotions', 'Announcements'],
    orientation: 'Landscape',
    idealFor: 'Lobby screens, waiting area',
    category: 'display'
  },
];

// App Card Component
function AppCard({ app, baseDomain }) {
  const [copied, setCopied] = useState(false);
  const Icon = app.icon;
  const fullUrl = `https://${app.subdomain}.${baseDomain}`;
  
  const handleCopy = () => {
    copyText(fullUrl, 'Link copied to clipboard!').then((ok) => {
      if (!ok) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleOpen = () => {
    window.open(fullUrl, '_blank');
  };

  return (
    <div className={`bg-white rounded-2xl border-2 ${app.borderColor} overflow-hidden hover:shadow-lg transition-all duration-200`}>
      {/* Header */}
      <div className={`${app.color} px-5 py-4 flex items-center gap-3`}>
        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
          <Icon className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-bold text-white truncate">{app.name}</h3>
          <p className="text-white/80 text-sm truncate">{app.subdomain}.{baseDomain}</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <p className="text-gray-600 mb-4 text-sm">{app.description}</p>
        
        {/* Features */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {app.features.map((feature, idx) => (
            <Badge 
              key={idx} 
              variant="outline" 
              className={`${app.colorLight} ${app.borderColor} text-xs`}
            >
              {feature}
            </Badge>
          ))}
        </div>

        {/* Meta info */}
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
          <span className="flex items-center gap-1">
            <Monitor className="w-3.5 h-3.5" />
            {app.orientation}
          </span>
          <span className="flex items-center gap-1 truncate">
            <Info className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{app.idealFor}</span>
          </span>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Button 
              onClick={handleOpen}
              className={`flex-1 ${app.color} hover:opacity-90 text-white`}
              size="sm"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Production
            </Button>
            <Button 
              variant="outline" 
              onClick={handleCopy}
              size="sm"
              className="px-3"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AppLinksPage() {
  const [baseDomain, setBaseDomain] = useState(() => {
    return localStorage.getItem('app_links_domain') || 'chain29.com';
  });
  const [showConfig, setShowConfig] = useState(false);
  const [tempDomain, setTempDomain] = useState(baseDomain);
  

  const handleSaveDomain = () => {
    setBaseDomain(tempDomain);
    localStorage.setItem('app_links_domain', tempDomain);
    setShowConfig(false);
    toast.success('Domain saved!');
  };

  const handleCopyAll = () => {
    const allLinks = PWA_APPS.map(app => 
      `${app.name}: https://${app.subdomain}.${baseDomain}`
    ).join('\n');
    copyText(allLinks, 'All links copied to clipboard!');
  };

  const staffApps = PWA_APPS.filter(a => a.category === 'staff');
  const customerApps = PWA_APPS.filter(a => a.category === 'customer');
  const displayApps = PWA_APPS.filter(a => a.category === 'display');

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Smartphone className="w-8 h-8 text-blue-600" />
              App Links
            </h1>
            <p className="text-gray-500 mt-1">
              Access all your restaurant apps from one place
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setTempDomain(baseDomain); setShowConfig(!showConfig); }}>
              <Settings className="w-4 h-4 mr-2" />
              Configure
            </Button>
            <Button onClick={handleCopyAll} className="bg-blue-600 hover:bg-blue-700">
              <Copy className="w-4 h-4 mr-2" />
              Copy All
            </Button>
          </div>
        </div>

        {/* Domain Config */}
        {showConfig && (
          <div className="bg-gray-50 border rounded-xl p-4 mb-6">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Your Domain
            </label>
            <div className="flex gap-2">
              <Input
                value={tempDomain}
                onChange={(e) => setTempDomain(e.target.value)}
                placeholder="yourdomain.com"
                className="max-w-xs"
              />
              <Button onClick={handleSaveDomain}>Save</Button>
              <Button variant="ghost" onClick={() => setShowConfig(false)}>Cancel</Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Enter your custom domain to generate correct app URLs
            </p>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
            <p className="text-3xl font-bold">{PWA_APPS.length}</p>
            <p className="text-sm text-blue-100">Total Apps</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
            <p className="text-3xl font-bold">{staffApps.length}</p>
            <p className="text-sm text-emerald-100">Staff Apps</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
            <p className="text-3xl font-bold">{customerApps.length}</p>
            <p className="text-sm text-purple-100">Customer Apps</p>
          </div>
          <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl p-4 text-white">
            <p className="text-3xl font-bold">{displayApps.length}</p>
            <p className="text-sm text-cyan-100">Display Apps</p>
          </div>
        </div>
      </div>

      {/* Staff Apps */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-2 h-6 bg-emerald-500 rounded-full" />
          Staff Apps
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {staffApps.map((app) => (
            <AppCard key={app.id} app={app} baseDomain={baseDomain} />
          ))}
        </div>
      </div>

      {/* Customer Apps */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-2 h-6 bg-purple-500 rounded-full" />
          Customer Apps
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {customerApps.map((app) => (
            <AppCard key={app.id} app={app} baseDomain={baseDomain} />
          ))}
        </div>
      </div>

      {/* Display Apps */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-2 h-6 bg-cyan-500 rounded-full" />
          Display Apps
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {displayApps.map((app) => (
            <AppCard key={app.id} app={app} baseDomain={baseDomain} />
          ))}
        </div>
      </div>

      {/* DNS Setup Info */}
      <div className="bg-gray-900 rounded-2xl p-6 text-white">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-blue-400" />
          DNS Configuration
        </h2>
        <p className="text-gray-300 mb-4">
          To enable these apps on your custom domain, add the following CNAME records:
        </p>
        <div className="bg-black/50 rounded-lg p-4 overflow-x-auto font-mono text-sm">
          {PWA_APPS.map(app => (
            <div key={app.id} className="text-green-400 mb-1">
              <span className="text-gray-500">#</span> {app.name}<br/>
              <span className="text-cyan-400">{app.subdomain}</span>.{baseDomain}  <span className="text-yellow-400">CNAME</span>  your-deployment.com
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-400 mt-4">
          Contact your domain registrar (GoDaddy, Namecheap, Cloudflare, etc.) to add these records.
        </p>
      </div>
    </div>
  );
}
